import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertsList } from '../../components/Alerts';
import {
    ACCESSORIES,
    APPARELBOTTOM,
    APPARELTOP,
    BREADCRUMB,
    CARDS,
    GIFTS,
    GRADPACKAGESGRID,
    JEWELRY,
    NOPACKAGEMSG,
    PACKAGEGRID,
    PAGETITLE,
    PROMOMSG,
    REGALIA,
    TERMSANDCONDITIONS
} from "../../constants/rootElements";
import { TermsAndConditionsProvider } from '../../context';
import { useFeeAlertInjectionScript, useGoogleAnalyticsHandler } from "../../hooks";
import { appendTermsAndConditionsLink } from '../../util/appendTermsAndConditionsLink';
import { extractBefore, EXTRACT_BEFORE_KEYWORDS } from '../../util/extractBefore';
import { gaGradCatalogBrowsePageLoad } from '../../util/googleAnalytics';
import { Breadcrumb } from '../breadcrumb';
import { TitleBar } from "../catalogBrowseTitle";
import GradAnnCardsTiles from './gradGroupsAndProducts';
import GradPackageTabs from './gradPackageTabs';
import GradProductTiles from './gradProductTiles';
import { packageFreeShippingDecorator } from './utils';

const HsGradPage = ({ appData }) => {
    const [pbData, setPbData] = useState({});
    const [giftData, setGiftData] = useState({});
    const [regaliaData, setRegaliaData] = useState({});
    const [apparelTopData, setApparelTopData] = useState({});
    const [apparelBottomData, setApparelBottomData] = useState({});
    const [jewelryData, setJewelryData] = useState({});
    const [accessoriesData, setAccessoriesData] = useState({});
    const [AnnCardsData, setAnnCardsData] = useState({});
    const [breadCrumbData, setBreadcrumbData] = useState(null);
    const [packageGridCGData, setPackageGridCGData] = useState({});
    const [packageGridData, setPackageGridData] = useState({});
    const [annAndCardsActionUrl, setAnnCardsActionUrl] = useState("");
    const [altImage, setAltImage] = useState("");
    const [promoMessages, setPromoMessages] = useState([]);
    const [feeAlertRequestData, setFeeAlertRequestData] = useState(null);
    const [googleAnalyticsRequestData, setGoogleAnalyticsRequestData] = useState(null);
    const [promoData, setPromoData] = useState({});
    const [uniquePromoCodes, setUniquePromoCodes] = useState([]);

    useFeeAlertInjectionScript(feeAlertRequestData)
    const googleAnalytics = useGoogleAnalyticsHandler(googleAnalyticsRequestData);

    const HsPackagesGrid = appData?.pageContentData?.find(data => (data.contentType === "Packages" && data.ctaCheck?.groupOsr === "PACKAGES"))
    const HsCGPackagesGrid = appData?.pageContentData?.find(data => (data.contentType === "Packages" && data.ctaCheck?.groupOsr === "REGALIA"))
    const HsGradCards = appData?.pageContentData?.find(data => data.contentType === "CatalogBrowseFeaturedProducts")
    const HsGradApparelTop = appData?.pageContentData?.find(data => (data.contentType === "ProductBrowseProducts" && data.ctaCheck?.groupOsr === "APPAREL_TOP"))
    const HsGradApparelBottom = appData?.pageContentData?.find(data => (data.contentType === "ProductBrowseProducts" && data.ctaCheck?.groupOsr === "APPAREL_BOTTOM"))
    const HsGradRegalia = appData?.pageContentData?.find(data => (data.contentType === "ProductBrowseProducts" && data.ctaCheck?.groupOsr === "REGALIA"))
    const HsGradGifts = appData?.pageContentData?.find(data => (data.contentType === "ProductBrowseProducts" && data.ctaCheck?.groupOsr === "GIFTS"))
    const HsGradJewelry = appData?.pageContentData?.find(data => (data.contentType === "ProductBrowseProducts" && data.ctaCheck?.groupOsr === "JEWELRY"))
    const HsGradAccessories = appData?.pageContentData?.find(data => (data.contentType === "ProductBrowseProducts" && data.ctaCheck?.groupOsr === "ACCESSORIES"))
    const HsGradBreadcrumb = Boolean(appData?.pageContentData?.find(data => data.contentType === "Navigation"))
    const HsCGBarBanner = appData?.pageContentData?.find(data => data.contentType === "CGBarBanner")
    const HsGradTitleBar = appData?.pageContentData?.find(data => data.contentType === "GradPackageBrowseTitle")
    const HsPaymentPromo = appData?.pageContentData?.find(data => data.contentType === "PaymentPromo")
    const ProductBrowsePromoMsg = Boolean(
		appData?.pageContentData?.find(
			(data) => data.contentType === 'ProductBrowsePromoMsg'
		)
	);

    useEffect(() => {
        const uniquePromoCode = uniquePromoCodes[0]?.promoCode;
        const thresholdValue = extractBefore(uniquePromoCode, EXTRACT_BEFORE_KEYWORDS.SENIORSHIP);
        const resolvedPromoData = JSON.stringify({
            promoCode: uniquePromoCode,
            ...(thresholdValue && {
                thresholdValue,
                promoDetailsDefaultRef: 'grad-default'
            })
        });
        setPromoData(resolvedPromoData);
    }, [uniquePromoCodes]);

    const termsAndConditionsLink = useMemo(() => !!appData?.PromoCodesData?.length && (
        <div className='d-flex pl-4 py-4'>
            <a className="jos-show-terms-and-conditions priceColor fontSize1X"
                style={{ display: "none" }}
                role="button"
                tabIndex="0"
                aria-haspopup="dialog"
                aria-expanded="false"
                data-promo={promoData}
            >
                * See details and exclusions
            </a>
        </div>
    ), [promoData, appData?.PromoCodesData])

    const getBreadcrumb = useCallback(() => {
        BREADCRUMB.render(<Breadcrumb pbData={breadCrumbData} />);
    }, [breadCrumbData])

    const getPackagesGrid = useCallback(() => {
        packageGridData?.packageItems?.length > 0 && packageGridData?.products?.length > 0 &&
        GRADPACKAGESGRID.render(
            <GradPackageTabs
                packageList={packageGridData?.packageItems}
                productList={packageGridData?.products}
                pageContentData={HsPackagesGrid}
                hsPaymentPromo={HsPaymentPromo}
                altImage={altImage}
                googleAnalytics={googleAnalytics}
            />
        );
    }, [HsPackagesGrid, packageGridData, HsPaymentPromo, googleAnalytics])

    const getCGPackagesGrid = useCallback(() => {
        packageGridCGData?.packageItems?.length > 0 && packageGridCGData?.products?.length > 0 &&
        PACKAGEGRID.render(
            <GradPackageTabs
                packageList={packageGridCGData?.packageItems?.filter(obj => obj.packageProductAvailability?.length > 0)}
                productList={[...packageGridCGData?.products?.filter(obj => obj.productType === HsCGPackagesGrid?.ctaCheck?.productType)]}
                pageContentData={HsCGPackagesGrid}
                hsPaymentPromo={HsPaymentPromo}
                altImage={altImage}
                googleAnalytics={googleAnalytics}
            />
        );
    }, [HsCGPackagesGrid, packageGridCGData, HsPaymentPromo, googleAnalytics])

    const getProductTileRegalia = useCallback((tileData, imageUrl, googleAnalytics) => {
        REGALIA.render(
          <TermsAndConditionsProvider>
              <GradProductTiles
                tileData={tileData}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [])

    const getProductTileApparelTop = useCallback((tileData, imageUrl, googleAnalytics) => {
        APPARELTOP.render(
          <TermsAndConditionsProvider>
              <GradProductTiles
                tileData={tileData}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [])

    const getProductTileApparelBottom = useCallback((tileData, imageUrl, googleAnalytics) => {
        APPARELBOTTOM.render(
          <TermsAndConditionsProvider>
              <GradProductTiles
                tileData={tileData}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [])

    const getProductTileJewelry = useCallback((tileData, imageUrl, googleAnalytics) => {
        JEWELRY.render(
          <TermsAndConditionsProvider>
              <GradProductTiles
                tileData={tileData}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [])

    const getProductTileAccessories = useCallback((tileData, imageUrl, googleAnalytics) => {
        ACCESSORIES?.render(
          <TermsAndConditionsProvider>
              <GradProductTiles
                tileData={tileData}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [])

    const getProductTileGift = useCallback((tileData, imageUrl, googleAnalytics) => {
        GIFTS.render(
          <TermsAndConditionsProvider>
              <GradProductTiles
                tileData={tileData}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [])

    const getAnnCardsTile = useCallback((tileData, pageContent, imageUrl, googleAnalytics) => {
        CARDS.render(
          <TermsAndConditionsProvider>
              <GradAnnCardsTiles
                tileData={tileData}
                pageContent={pageContent}
                shopAllUrl={annAndCardsActionUrl}
                promoCodes={appData.PromoCodesData}
                altImage={imageUrl}
                googleAnalytics={googleAnalytics}
                uniquePromoCodes={uniquePromoCodes}
                setUniquePromoCodes={setUniquePromoCodes}
              />
          </TermsAndConditionsProvider>
        );
    }, [annAndCardsActionUrl])

    const getTitleBar = useCallback(() => {
        PAGETITLE.render(
            <TitleBar
                pageContent={HsGradTitleBar}
                affiliationData={appData?.affiliationData}
            />
        );
    }, [])

    const getPromoMsg = useCallback(() => {
        const messagesWithLink = appendTermsAndConditionsLink(promoMessages, 'grad-default');
        PROMOMSG.render(<AlertsList messages={messagesWithLink} />);
    }, [promoMessages]);

    const getTermsAndConditions = useCallback(() => {
        appData?.PromoCodesData && TERMSANDCONDITIONS.render(termsAndConditionsLink)
    }, [termsAndConditionsLink, appData?.PromoCodesData])

    useEffect(()=>{
        gaGradCatalogBrowsePageLoad();
    },[])

    useEffect(() => {
        Object.keys(appData.catalogBrowseData).forEach((key) => {
            if (key !== "metaData" && appData.catalogBrowseData[key]?.productGroups) {
                Object.keys(appData.catalogBrowseData[key]?.productGroups)?.map((catObj) => {
                    const customerNumber = appData.catalogBrowseData.metaData.customerNumber
                    const catalog = appData.catalogBrowseData[key]
                    setFeeAlertRequestData({
                        containerSelector: '#promoMsg',
                        customerId: customerNumber,
                        catalogOsr: catalog.catalogOsr,
                    })
                    setGoogleAnalyticsRequestData({
                        customerNumber,
                        publishedCatalogId: catalog.publishedCatalogId,
                        sourceCatalogType: catalog.sourceCatalogType,
                        catalogOsr: catalog.catalogOsr
                    });
                    setPbData(appData.catalogBrowseData[key]?.productGroups)
                    setPromoMessages(appData.catalogBrowseData[key]?.promoMessagesV2);
                    setBreadcrumbData(appData.catalogBrowseData[key]);
                    const groupData = { ...appData.catalogBrowseData[key].productGroups[catObj] }
                    if (groupData.groupOsr === HsGradGifts.ctaCheck.groupOsr) {
                        setGiftData(groupData)
                    } else if (groupData.groupOsr === HsCGPackagesGrid?.ctaCheck?.groupOsr) {
                        const regaliaTileData = { 
                            ...groupData, 
                            products: groupData.products || []
                        }
                        const packageList = regaliaTileData.products.filter(obj => obj.productType === "PACKAGE")
                        regaliaTileData.products = regaliaTileData.products.filter(obj => obj.productType !== "PACKAGE")
                        const CGBarBannerProdRefCodes = HsCGBarBanner.ctaCheck?.prodRefCode.split(',') || []
                        const productsTrackedByCGBarBanner = regaliaTileData.products.filter(obj => CGBarBannerProdRefCodes.includes(obj.prodRefCode))

                        if (groupData.groupType === "PACKAGES") {
                            const freeShippingPromo = appData.catalogBrowseData[key]?.promoMessagesV2?.find(promo => promo.promoCode?.includes('SENIORSHIP'))
                            const freeShippingPromoThresholdValue = Number.parseInt(freeShippingPromo?.promoCode.split('SENIORSHIP')[0], 10)
                            packageFreeShippingDecorator(groupData, freeShippingPromoThresholdValue)

                            setPackageGridCGData(groupData)
                            setRegaliaData(regaliaTileData)
                        } else if (groupData.groupOsr === HsGradRegalia?.ctaCheck?.groupOsr) {
                            setRegaliaData(regaliaTileData)
                        }

                        if(packageList.length === 0 && productsTrackedByCGBarBanner.length === 0) {
                            // show no-package-message if no package and no available products tracked by "C&GBanner" content
                            NOPACKAGEMSG.render(<div className='container-fluid' dangerouslySetInnerHTML={{__html: HsCGBarBanner?.content}} />);
                        }
                    } else if (groupData.groupOsr === HsPackagesGrid?.ctaCheck?.groupOsr && groupData.groupType === "PACKAGES") {
                        const freeShippingPromo = appData.catalogBrowseData[key]?.promoMessagesV2?.find(promo => promo.promoCode?.includes('SENIORSHIP'))
                        const freeShippingPromoThresholdValue = Number.parseInt(freeShippingPromo?.promoCode.split('SENIORSHIP')[0], 10)
                        packageFreeShippingDecorator(groupData, freeShippingPromoThresholdValue)

                        setPackageGridData(groupData)
                    } else if (groupData.groupOsr === HsGradApparelTop?.ctaCheck?.groupOsr) {
                        setApparelTopData(groupData)
                    } else if (groupData.groupOsr === HsGradApparelBottom?.ctaCheck?.groupOsr) {
                        setApparelBottomData(groupData)
                    } else if (groupData.groupOsr === HsGradJewelry?.ctaCheck?.groupOsr) {
                        setJewelryData(groupData)
                    } else if (groupData.groupOsr === HsGradAccessories?.ctaCheck?.groupOsr) {
                        setAccessoriesData(groupData)
                    }
                    return true
                })
            }
            return true
        })
    }, [appData.catalogBrowseData, HsGradJewelry, HsGradApparelTop, HsGradApparelBottom, HsGradRegalia, HsCGPackagesGrid, HsGradGifts, HsPackagesGrid, HsCGBarBanner])

    useEffect(()=>{
        setAltImage(HsGradApparelTop?.imageUrl)
    },[HsGradApparelTop])

    useEffect(() => {
        let cardGroups = []
        HsGradCards && Object.keys(HsGradCards)?.length > 0 && HsGradCards.featuredProducts?.length > 0 &&
            HsGradCards.featuredProducts?.sort((a, b) => a.ordersequence - b.ordersequence)?.map((ctaData) =>
                pbData && Object.keys(pbData)?.map((catObj) => {
                    if (pbData[catObj]?.groupOsr === ctaData.groupOsr) {
                        if (ctaData.productOsr) {
                            (ctaData.considerGroupActionUrl !== "false") && setAnnCardsActionUrl(pbData[catObj]?.actionUrl);
                            let productObjs = pbData[catObj]?.products?.length > 0 && pbData[catObj]?.products?.filter((productObj) => {
                                const regexValue = new RegExp(ctaData.productOsr)
                                return (regexValue.test(productObj.productOsr));
                            })
                            if(productObjs && productObjs.length ) {
                                productObjs?.sort((a,b)=>{
                                    if (a.orderSeq && b.orderSeq) {
                                        if(a.orderSeq === b.orderSeq) {
                                            return a.displayName > b.displayName ? 1 : -1
                                        }
                                        return  a.orderSeq - b.orderSeq
                                    } else if (a.orderSeq) {
                                        return -1
                                    } else {
                                        return 1
                                    }
                                })
                                cardGroups.push(...productObjs)
                            }
                        } else {
                            cardGroups.push(pbData[catObj])
                        }
                    }
                    return true
                })
            )
        setAnnCardsData(cardGroups)
    }, [pbData, HsGradCards])

    return (
        <>
            {
                HsGradTitleBar && Object.keys(HsGradTitleBar)?.length > 0 && getTitleBar()
            }
            {
                ProductBrowsePromoMsg && promoMessages?.length > 0 && getPromoMsg()
            }
            {
                HsGradBreadcrumb && breadCrumbData && Object.keys(breadCrumbData)?.length > 0 && getBreadcrumb()
            }
            {
                HsPackagesGrid && packageGridData && Object.keys(packageGridData)?.length > 0 && getPackagesGrid()
            }
            {
                HsCGPackagesGrid && packageGridCGData && Object.keys(packageGridCGData)?.length > 0 && getCGPackagesGrid()
            }
            {
                HsGradRegalia && regaliaData && Object.keys(regaliaData)?.length > 0 && getProductTileRegalia(regaliaData, altImage, googleAnalytics)
            }
            {
                HsGradCards && Object.keys(HsGradCards)?.length > 0 && AnnCardsData && Object.keys(AnnCardsData)?.length > 0 && getAnnCardsTile(AnnCardsData, HsGradCards, altImage, googleAnalytics)
            }
            {
                HsGradApparelTop && apparelTopData && Object.keys(apparelTopData)?.length > 0 && getProductTileApparelTop(apparelTopData, altImage, googleAnalytics)
            }
            {
                HsGradApparelBottom && apparelBottomData && Object.keys(apparelBottomData)?.length > 0 && getProductTileApparelBottom(apparelBottomData, altImage, googleAnalytics)
            }
            {
                HsGradJewelry && jewelryData && Object.keys(jewelryData)?.length > 0 && getProductTileJewelry(jewelryData, altImage, googleAnalytics)
            }
            {
                HsGradAccessories && accessoriesData && Object.keys(accessoriesData)?.length > 0 && getProductTileAccessories(accessoriesData, altImage, googleAnalytics)
            }
            {
                HsGradGifts && giftData && Object.keys(giftData)?.length > 0 && getProductTileGift(giftData, altImage, googleAnalytics)
            }
            {
                getTermsAndConditions()
            }
        </>
    )
}
export default HsGradPage